import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Config_Api from "./config";
import { getCartToken, storeCartToken } from "./cartToken";
import { getCurrentAuthToken } from "./authToken";

const CartContext = createContext();

const getAuthToken = () => getCurrentAuthToken();
const getAuthIdentity = () => getAuthToken() || "guest";

const getRequestConfig = () => {
  const token = getAuthToken();
  const cartToken = getCartToken();
  const headers = {
    "X-Cart-Token": cartToken,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const mapApiItemToUi = (item) => {
  const product = item?.product || {};
  const variant = item?.variant || {};
  const rawAttributes = variant?.options || variant?.attributes || {};
  const attributes = Array.isArray(rawAttributes)
    ? rawAttributes
    : (rawAttributes && typeof rawAttributes === "object")
      ? Object.entries(rawAttributes).map(([key, value]) => ({ key, value }))
      : [];
  const image =
    variant?.image_url ||
    variant?.image ||
    product?.image_url ||
    product?.image ||
    "/images/gh.webp";

  return {
    cartItemId: item?.id,
    productId: item?.product_id,
    type: "oil",
    name: product?.name || "Product",
    slug: product?.slug || null,
    selectedQty: {
      id: item?.product_variant_id || item?.product_id,
      label: variant?.label || "Variant",
      attributes,
      price: toNumber(item?.price),
      image,
    },
    qty: toNumber(item?.quantity) || 1,
    image,
  };
};

const emptySummary = {
  item_count: 0,
  subtotal: 0,
  discount: 0,
  shipping_charge: 0,
  total: 0,
  coupon_code: null,
};

export const CartProvider = ({ children }) => {
  const [authIdentity, setAuthIdentity] = useState(() => getAuthIdentity());
  const [cart, setCart] = useState([]);
  const [cartSummary, setCartSummary] = useState(emptySummary);
  const [loadingCart, setLoadingCart] = useState(false);

  const setCartFromPayload = (responseData) => {
    const payload = responseData?.data || {};
    const apiItems = (payload?.items || []).map(mapApiItemToUi);
    const summary = payload?.summary || {};

    const incomingToken = responseData?.cart_token;
    if (incomingToken) {
      storeCartToken(incomingToken, authIdentity);
    }

    setCart(apiItems);
    setCartSummary({
      item_count: toNumber(summary?.item_count),
      subtotal: toNumber(summary?.subtotal),
      discount: toNumber(summary?.discount),
      shipping_charge: toNumber(summary?.shipping_charge),
      total: toNumber(summary?.total),
      coupon_code: summary?.coupon_code || null,
    });
  };

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const currentIdentity = getAuthIdentity();
      setAuthIdentity((prev) => (prev === currentIdentity ? prev : currentIdentity));
    }, 800);

    return () => window.clearInterval(intervalId);
  }, []);

  const syncCart = async () => {
    try {
      setLoadingCart(true);
      const response = await axios.get(`${Config_Api}/cart`, getRequestConfig());
      setCartFromPayload(response?.data || {});
    } catch (error) {
      setCart([]);
      setCartSummary(emptySummary);
    } finally {
      setLoadingCart(false);
    }
  };

  useEffect(() => {
    syncCart();
  }, [authIdentity]);

  const addToCart = async (product, selectedQty, qty = 1) => {
    const isRealVariant =
      Boolean(selectedQty?.isVariant) ||
      (Array.isArray(selectedQty?.options) &&
        selectedQty.options.length > 0 &&
        Number(selectedQty?.id) !== Number(product?.id));

    const payload = {
      product_id: product?.id,
      product_variant_id: isRealVariant ? selectedQty?.id : null,
      quantity: qty,
      price: toNumber(selectedQty?.price),
      cart_token: getCartToken(),
    };

    try {
      await axios.post(`${Config_Api}/cart/items`, payload, getRequestConfig());
      await syncCart();
      return true;
    } catch (error) {
      return false;
    }
  };

  const updateQty = async (productId, qtyId, qty) => {
    if (qty < 1) return;

    const matched = cart.find(
      (item) => item.productId === productId && item.selectedQty?.id === qtyId
    );

    if (!matched?.cartItemId) return;

    try {
      await axios.patch(
        `${Config_Api}/cart/items/${matched.cartItemId}`,
        {
          quantity: qty,
          cart_token: getCartToken(),
        },
        getRequestConfig()
      );

      await syncCart();
    } catch (error) {
    }
  };

  const removeFromCart = async (productId, qtyId) => {
    const matched = cart.find(
      (item) => item.productId === productId && item.selectedQty?.id === qtyId
    );

    if (!matched?.cartItemId) return;

    try {
      await axios.delete(`${Config_Api}/cart/items/${matched.cartItemId}`, {
        ...getRequestConfig(),
        data: {
          cart_token: getCartToken(),
        },
      });

      await syncCart();
    } catch (error) {
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${Config_Api}/cart/clear`, {
        ...getRequestConfig(),
        data: {
          cart_token: getCartToken(),
        },
      });

      setCart([]);
      setCartSummary(emptySummary);
    } catch (error) {
    }
  };

  const applyCoupon = async (code) => {
    const payload = {
      code,
      cart_token: getCartToken(),
    };

    try {
      const response = await axios.post(
        `${Config_Api}/cart/coupon/apply`,
        payload,
        {
          ...getRequestConfig(),
          validateStatus: (status) => status >= 200 && status < 500,
        }
      );

      if (response?.data?.success) {
        setCartFromPayload(response?.data || {});
      }

      return {
        success: Boolean(response?.data?.success),
        message: response?.data?.message || 'Unable to apply coupon.',
      };
    } catch (error) {
      const validationErrors = error?.response?.data?.errors;
      const firstValidationMessage = validationErrors && typeof validationErrors === 'object'
        ? Object.values(validationErrors).flat().find(Boolean)
        : null;

      return {
        success: false,
        message: firstValidationMessage || error?.response?.data?.message || 'Unable to apply coupon.',
      };
    }
  };

  const removeCoupon = async () => {
    try {
      const response = await axios.delete(
        `${Config_Api}/cart/coupon`,
        {
          ...getRequestConfig(),
          data: {
            cart_token: getCartToken(),
          },
          validateStatus: (status) => status >= 200 && status < 500,
        },
      );

      if (response?.data?.data) {
        setCartFromPayload(response.data);
      } else {
        await syncCart();
      }

      if (!response?.data?.success) {
        return {
          success: false,
          message: response?.data?.message || 'Unable to remove coupon.',
        };
      }

      return {
        success: true,
        message: response?.data?.message || 'Coupon removed successfully.',
      };
    } catch (error) {
      await syncCart();

      return {
        success: false,
        message: error?.response?.data?.message || 'Unable to remove coupon.',
      };
    }
  };

  const cartCount = useMemo(() => {
    if (toNumber(cartSummary?.item_count) > 0) {
      return toNumber(cartSummary.item_count);
    }

    return cart.reduce((sum, item) => sum + toNumber(item.qty), 0);
  }, [cart, cartSummary]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartSummary,
        addToCart,
        updateQty,
        removeFromCart,
        applyCoupon,
        removeCoupon,
        clearCart,
        syncCart,
        loadingCart,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
export { getCartToken };
